import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Field, useFormikContext } from 'formik';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import {
  LegacyInputTextComponent,
  DropdownWithValidation,
  TextAreaWithValidation,
} from '../../../../components/prime-formik';
import FormLanguageSelector from '../../../../components/formik-components/FormLanguageSelector';
import { RESPONDENT_TYPE_ARRAY, GENDER_ARRAY } from '../../../../utils/Gr8piConfig';
import useSubscriptionExpiredRestrictions from '../../../../hooks/useSubscriptionExpiredRestrictions';
import { RespondentType } from '../../../../shared/Constants';

const messages = defineMessages({
  fNamePlaceholder: {
    id: 'placeholder.firstName',
    defaultMessage: 'First name',
  },
  lNamePlaceholder: {
    id: 'placeholder.lastName',
    defaultMessage: 'Last name',
  },
  emailPlaceholder: {
    id: 'placeholder.email',
    defaultMessage: 'Email',
  },
  choose: {
    id: 'choose',
    defaultMessage: 'Choose',
  },
  gender_M: {
    id: 'attrs.gender.M',
    defaultMessage: 'Male',
  },
  gender_F: {
    id: 'attrs.gender.F',
    defaultMessage: 'Female',
  },
  gender_U: {
    id: 'attrs.gender.U',
    defaultMessage: 'Unspecified',
  },
  respondent_type_INTERNAL: {
    id: 'attrs.candidateType.INTERNAL',
    defaultMessage: 'Internal Employee',
  },
  respondent_type_EXTERNAL: {
    id: 'attrs.candidateType.EXTERNAL',
    defaultMessage: 'External Respondent',
  },
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  save: { id: 'actions.save', defaultMessage: 'Save' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
});

const RespondentEditForm = ({ preferedLocales, toggleEdit, respondent, showEmploymentConfirmationModal }) => {
  const { formatMessage } = useIntl();
  const { handleSubmit, resetForm, isSubmitting, setFieldValue } = useFormikContext();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const genderOptions = GENDER_ARRAY.map((key) => ({
    value: key,
    label: formatMessage(messages[`gender_${key}`]),
  }));
  const respondentTypes = RESPONDENT_TYPE_ARRAY.map((key) => ({
    value: key,
    label: formatMessage(messages[`respondent_type_${key}`]),
  }));

  return (
    <Panel header={`${respondent.firstName} ${respondent.lastName}`}>
      <form className="form" onSubmit={handleSubmit}>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="firstName">
              <FormattedMessage id="lbl.first_name" defaultMessage="First Name" />
            </label>
          </div>
          <div className="p-col-12">
            <Field
              id="firstName"
              name="firstName"
              component={LegacyInputTextComponent}
              placeholder={formatMessage(messages.fNamePlaceholder)}
            />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="lastName">
              <FormattedMessage id="lbl.last_name" defaultMessage="Last Name" />
            </label>
          </div>
          <div className="p-col-12">
            <Field
              type="text"
              name="lastName"
              id="lastName"
              placeholder={formatMessage(messages.lNamePlaceholder)}
              component={LegacyInputTextComponent}
            />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="email">
              <FormattedMessage id="attrs.email" defaultMessage="Email" />
            </label>
            <Field
              id="email"
              name="email"
              component={LegacyInputTextComponent}
              placeholder={formatMessage(messages.emailPlaceholder)}
            />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <div className="form-group">
              <label className="field-label" htmlFor="respondent-type">
                <FormattedMessage id="attrs.gender" defaultMessage="Gender" />
              </label>
              <Field
                id="gender"
                name="gender"
                component={DropdownWithValidation}
                options={genderOptions}
                placeholder={`- ${formatMessage(messages.choose)} -`}
              />
            </div>
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <div className="form-group">
              <label className="field-label" htmlFor="respondent-type">
                <FormattedMessage id="attrs.candidateType" defaultMessage="Respondent type" />
              </label>
              <Field
                id="type"
                name="type"
                component={DropdownWithValidation}
                options={respondentTypes}
                placeholder={`- ${formatMessage(messages.choose)} -`}
                onChange={(event) => {
                  if (respondent.wasAddedViaSuperMatch && respondent.type === RespondentType.EXTERNAL) {
                    showEmploymentConfirmationModal();
                  } else {
                    setFieldValue('type', event.value);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="locale">
              <FormattedMessage id="attrs.preferredLanguage" defaultMessage="Preferred language" />
            </label>
            <Field id="locale" name="locale" component={FormLanguageSelector} options={preferedLocales} />
          </div>
        </div>
        <div className="p-grid p-fluid">
          <div className="p-col-12">
            <label className="field-label" htmlFor="notes">
              <FormattedMessage id="attrs.notes" defaultMessage="Notes" />
            </label>
            <Field className="form-control" id="notes" name="notes" component={TextAreaWithValidation} rows={5} />
          </div>
        </div>
        <div className="p-grid">
          <div className="p-sm-12">
            <Button
              type="submit"
              disabled={isSubmitting || subscriptionIsExpired}
              label={formatMessage(messages.save)}
              icon={isSubmitting ? 'pi pi-spin pi-spinner' : ''}
            />
            <Button
              type="button"
              className="p-button-secondary"
              disabled={isSubmitting}
              label={formatMessage(messages.cancel)}
              onClick={() => {
                resetForm();
                toggleEdit('core');
              }}
            />
          </div>
        </div>
      </form>
    </Panel>
  );
};

RespondentEditForm.propTypes = {
  preferedLocales: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleEdit: PropTypes.func.isRequired,
  respondent: PropTypes.objectOf(PropTypes.any).isRequired,
  showEmploymentConfirmationModal: PropTypes.func.isRequired,
};

export default RespondentEditForm;
